import { BrowserRouter, NavLink, Navigate, Route, Routes } from "react-router-dom";
import { useAuth } from "./auth/AuthContext";
import { useFeatureFlags } from "./contexts/FeatureFlagsContext";
import { Dashboard, type DashboardTab, CORE_DASHBOARD_TABS, ADMIN_DASHBOARD_TABS } from "./components/Dashboard";
import { Header } from "./components/Header";
import { ReportsPage } from "./components/ReportsPage";
import { JobBoardPage } from "./components/JobBoardPage";
import { MyProfilePage } from "./components/MyProfilePage";
import { AdminDashboard } from "./components/AdminDashboard";
import { MyDashboardPage } from "./components/MyDashboardPage";
import { LogoutPage } from "./components/LogoutPage";

const OPS_DASHBOARD_TABS: DashboardTab[] = ["people", "awsRrs", "assignments"];

function App() {
  const { user, loading, signIn, signOut, hasRole } = useAuth();
  const { flags, loading: flagsLoading } = useFeatureFlags();
  const appEnv = (import.meta.env.VITE_APP_ENV as string | undefined)?.trim().toLowerCase();
  const envBadge = appEnv === "dev" ? <span className="app-env-indicator">[Dev]</span> : null;
  const headerTitle = (
    <>
      AWS COE Staffing Canvas
      {envBadge ? <> {envBadge}</> : null}
    </>
  );

  if (loading || flagsLoading) {
    return (
      <div className="app-shell centered">
        <p>Loading…</p>
      </div>
    );
  }

  if (!user) {
    return (
      <BrowserRouter>
        <div className="app-shell auth-screen">
          <Header title={headerTitle} subtitle="Authenticate to manage staffing data" />
          <div className="panel auth-card">
            <p>
              Sign in with your AWS Cognito credentials to access people, project, assignment, and reporting features.
            </p>
            <button type="button" className="primary" onClick={() => void signIn()}>
              Sign in with Cognito
            </button>
          </div>
        </div>
      </BrowserRouter>
    );
  }

  const isRoot = hasRole("root");
  const isAdminRole = hasRole("admin");
  const isAdmin = isRoot || isAdminRole;
  const isOps = hasRole("ops") && !isAdmin;
  const isConsultant = hasRole("consultant") && !isAdmin;

  const canAccessDashboard = (isAdmin || isOps) && flags.dashboard;
  const canAccessMyDashboard = isConsultant && flags.myDashboard;
  const canAccessReports = isAdmin && flags.reports;
  const canAccessAdmin = (isRoot || isAdminRole) && flags.admin;
  const canAccessJobBoard = (isAdmin || isConsultant) && flags.jobBoard;

  let dashboardTabs: DashboardTab[] = [];
  if (isAdminRole || isRoot) {
    dashboardTabs = ADMIN_DASHBOARD_TABS;
  } else if (isOps) {
    dashboardTabs = OPS_DASHBOARD_TABS;
  }

  const defaultRoute = canAccessDashboard
    ? "/"
    : canAccessMyDashboard
      ? "/my-dashboard"
      : canAccessJobBoard
        ? "/jobboard"
        : canAccessReports
          ? "/reports"
          : "/";

  return (
    <BrowserRouter>
      <div className="app-shell">
        <Header
          title={headerTitle}
          subtitle="Manage consultants, requisitions, and assignments"
          userName={user.email ?? user.username}
          onSignOut={() => void signOut()}
        />
        <nav className="primary-nav">
          {canAccessDashboard && (
            <NavLink to="/" end className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}>
              Dashboard
            </NavLink>
          )}
          {canAccessMyDashboard && (
            <NavLink to="/my-dashboard" className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}>
              My Dashboard
            </NavLink>
          )}
          {canAccessJobBoard && (
            <NavLink to="/jobboard" className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}>
              Job Board
            </NavLink>
          )}
          {canAccessReports && (
            <NavLink to="/reports" className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}>
              Reports
            </NavLink>
          )}
          {canAccessAdmin && (
            <NavLink to="/admin" className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}>
              Admin
            </NavLink>
          )}
        </nav>
        <main className="route-content">
          <Routes>
            <Route
              path="/"
              element={canAccessDashboard ? <Dashboard allowedTabs={dashboardTabs} /> : <Navigate to={defaultRoute} replace />}
            />
            <Route
              path="/jobboard"
              element={canAccessJobBoard ? <JobBoardPage /> : <Navigate to={defaultRoute} replace />}
            />
            <Route
              path="/my-dashboard"
              element={canAccessMyDashboard ? <MyDashboardPage /> : <Navigate to={defaultRoute} replace />}
            />
            <Route
              path="/reports"
              element={canAccessReports ? <ReportsPage /> : <Navigate to={defaultRoute} replace />}
            />
            <Route path="/profile" element={<MyProfilePage />} />
            <Route
              path="/admin"
              element={canAccessAdmin ? <AdminDashboard /> : <Navigate to={defaultRoute} replace />}
            />
            <Route path="/logout" element={<LogoutPage />} />
            <Route path="*" element={<Navigate to={defaultRoute} replace />} />
          </Routes>
        </main>
      </div>
    </BrowserRouter>
  );
}

export default App;
